import {useConfig} from '@aboutyou/config';
import {StreamResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/hub_page/service.types';
import {
  FONT_SIZES,
  FONT_WEIGHTS,
  HorizontalSliderProps,
  SliderPagination,
  extraLargeScreensLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {useActivatePreselectedBestCouponABTest} from '../../Coupons/useActivatePreselectedBestCouponABTest';
import {HeadlineWrapper, OutfitSlider} from '../../OutfitSlider/OutfitSlider';
import {Brands} from '../Sections/Brands/Brands';
import {DiscoverCategory} from '../Sections/DiscoverCategory/DiscoverCategory';
import {HeroBrandsSlider} from '../Sections/HeroBrandsSlider/HeroBrandsSlider';
import {ReleaseBanner} from '../Sections/ReleaseBanner/ReleaseBanner';
import {SubCategoriesSlider} from '../Sections/SubCategoriesSlider/SubCategoriesSlider';
import {CustomComponents} from '../types';

interface StreamTileListProps extends CustomComponents {
  items: StreamResponse[];
  onAddToBasketCtaClick?: ({
    productId,
    locationId,
    couponPromotionId,
  }: {
    productId: number;
    locationId?: string;
    couponPromotionId?: string;
  }) => void;
}

export const StreamTileList: React.FC<StreamTileListProps> = ({
  items,
  SliderComponent,
  ProductsComponent,
  onAddToBasketCtaClick,
}) => {
  const {device} = useConfig();
  useActivatePreselectedBestCouponABTest();

  return (
    <StreamTile>
      {items.map((item, index) => {
        switch (item.section.type?.$case) {
          case 'spotlight':
            return (
              <SliderComponent
                key={`spotlight-${index}`}
                {...item.section.type.spotlight}
              />
            );
          case 'products':
            return (
              <ProductsComponent
                key={`products-${index}`}
                streamPosition={index}
                {...item.section.type.products}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                trackComponentConfig={{
                  location_id:
                    item.section.type.products.promotionTracker.locationId ||
                    'products',
                }}
              />
            );
          case 'outfitSlider': {
            const outfitSliderConfig: Partial<HorizontalSliderProps> =
              device === 'desktop'
                ? {
                    slideCount: 2.7,
                    slideCountLarge: 3.7,
                    hideSideButtons: true,
                  }
                : {hideSideButtons: true};

            return (
              <StyledOutfitSlider
                key={`outfitSLider-${index}`}
                outfitSliderData={item.section.type.outfitSlider}
                streamPosition={index}
                SlideControlComponent={
                  device === 'desktop' ? SliderPagination : undefined
                }
                SlideControlComponentBottom={
                  device === 'mobile' ? SliderPagination : undefined
                }
                {...outfitSliderConfig}
              />
            );
          }
          case 'brands':
            return (
              <Brands
                key={`brands-${index}`}
                {...item.section.type.brands}
                streamPosition={index}
              />
            );
          case 'heroBrands':
            return (
              <HeroBrandsSlider
                {...item.section.type.heroBrands}
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                key={`heroBrands-${index}`}
                trackComponentConfig={{
                  location_id: 'heroBrands',
                }}
              />
            );
          case 'discoverCategory':
            // mobile only
            return (
              <DiscoverCategory
                {...item.section.type.discoverCategory}
                streamPosition={index}
                key={`discoveryCategory-${index}`}
              />
            );
          case 'releaseBanner':
            // mobile only
            return (
              <ReleaseBanner
                {...item.section.type.releaseBanner}
                key={`releaseBanner-${index}`}
              />
            );
          case 'subCategories':
            // mobile only
            return (
              <SubCategoriesSlider
                elements={item.section.type?.subCategories.elements}
                key={`subCategories-${index}`}
              />
            );
          default:
            return null;
        }
      })}
    </StreamTile>
  );
};

const StreamTile = styled.section`
  --gap: 40px;
  ${tabletAndUpLinaria('--gap: 64px;')}
  ${extraLargeScreensLinaria('--gap: 80px;')}

  > * {
    margin-bottom: var(--gap);
  }
`;

const StyledOutfitSlider = styled(OutfitSlider)`
  ${HeadlineWrapper} {
    align-items: unset;
    text-transform: unset;
    gap: 0;
    margin-bottom: -8px;

    > span {
      text-transform: uppercase;
    }

    ${tabletAndUpLinaria(`
      gap: 8px;
      font-size: ${FONT_SIZES.px32};
      font-weight: ${FONT_WEIGHTS.heavy};

      > span {
        font-size: ${FONT_SIZES.px12};
        font-weight: ${FONT_WEIGHTS.mediumHeavy};
      }
    `)}
  }
`;
